import type { MouseEvent } from 'react';
import cx from 'clsx';
import { useToast } from '@kcd/design-system';

import { CHECKOUT_ROUTER, VENDORS_ROUTER } from '~/constants/router';
import { useUpdateECoponQuick } from '~/domains/cart/queries';
import ProductPrice from '~/domains/products/components/ProductPrice';
import ProductImage from '~/domains/products/ProductImage';
import { useTrackProductEventProps } from '~/hooks/event';
import { useLinkComponent } from '~/hooks/mobileApp';
import { usePushWithQuery } from '~/hooks/url';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import type { UpdateECouponQuickReqParams } from '~/pages/vendors/[vendorId]/products/[offerId]/components/ECoupon/ECouponOrderButton';
import type { OAISchema } from '~/types/openapi';

import PointOnly12Icon from '~/assets/icons/point_only_12x12.svg';

type Props = {
  item: OAISchema['MarketSelectProductResponseV2'];
  eventOption?: Record<string, unknown>;
  className?: string;
  reverseCouponDiscountedPrice?: boolean;
};

const EventECouponProductButtonCard = ({
  item,
  eventOption,
  className,
  reverseCouponDiscountedPrice = false,
}: Props) => {
  const toast = useToast();
  const LinkComponent = useLinkComponent();
  const [props] = useTrackProductEventProps(item, eventOption);
  const nextPush = usePushWithQuery();
  const updateQuickECoupon = useUpdateECoponQuick();

  const {
    vendorId,
    offerId,
    mainImages,
    isOutOfStock,
    productName,
    status,
    eCouponInfo,
  } = item;

  const onClickPurchase = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const requestBody: UpdateECouponQuickReqParams = {
      offerId: offerId!,
    };

    try {
      await updateQuickECoupon({
        requestBody,
      });
      void nextPush(CHECKOUT_ROUTER.eCouponRoot, false);
    } catch (error) {
      if (error instanceof OAIApiError) {
        const errMessage = getOAIErrorMessage(error);
        toast(errMessage, {
          offset: 86,
        });
      }
      throw error;
    }
  };

  return (
    <LinkComponent href={VENDORS_ROUTER.productDetail(vendorId, offerId!)}>
      <div
        role="presentation"
        {...props}
        className={cx([
          'rounded-12 border border-gray-200 bg-gray-000 overflow-hidden',
          className,
        ])}
      >
        <ProductImage
          layout="fill"
          src={mainImages![0]}
          alt={productName}
          isOutOfStock={isOutOfStock}
          status={status}
          className="!border-none"
        />
        <div className="px-8 py-12 pt-6">
          <div className="min-h-[60px]">
            {eCouponInfo?.eCouponProductType === 'FREE_POINT_ONLY' && (
              <span className="mb-4 flex items-center gap-4 text-blue-600 typo-footnote300-semibold">
                <PointOnly12Icon />
                포인트 전용 상품
              </span>
            )}
            <p
              className="mb-4 line-clamp-2 whitespace-pre-line leading-[18px] text-normal"
              style={{ fontSize: '14px' }}
            >
              {productName}
            </p>
            <ProductPrice {...item} reverse={reverseCouponDiscountedPrice} />
          </div>
          <button
            type="button"
            onClick={onClickPurchase}
            className="mt-8 flex w-full justify-center rounded-12 !bg-primary-regular py-[5px] text-gray-100 typo-paragraph100-semibold"
          >
            바로구매
          </button>
        </div>
      </div>
    </LinkComponent>
  );
};

export default EventECouponProductButtonCard;
